import { ChoiceButtonInput, ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Message } from '@app/app/_components/Message/Message';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { PaymentSchedule } from '@app/domain/model/eshop/life/PaymentSchedule';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { PaymentScheduleDetailsView } from '@app/app/esales/life/_sections/steps/PaymentScheduleDetailsView';
import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { LifeCalculateResult } from '@app/domain/model/eshop/life/LifeCalculateResult';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';

interface Props {
    calculation: Nullable<LifeCalculateResult>;
    onPaymentScheduleChange: (scheduleToCalculate?: string) => Promise<LifeCalculateResult | null>;
    sessionData: Nullable<SessionResponse>;
}

export function PaymentScheduleStep({ onPaymentScheduleChange, calculation, sessionData }: Props) {
    const t = useTranslations();
    const [selectedScheduleForDetails, setSelectedScheduleForDetails] = useState<Nullable<string>>(null);
    const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);
    const [previewCalculation, setPreviewCalculation] = useState<Nullable<LifeCalculateResult>>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const defaultPaymentSchedule = sessionData?.firstStep?.paymentSchedule || PaymentSchedule.Fixed;

    const debouncedPaymentScheduleChange = useDebouncedCallback(() => {
        void onPaymentScheduleChange();
    }, 500);

    const handlePaymentScheduleChange = useCallback(() => {
        debouncedPaymentScheduleChange();
    }, [debouncedPaymentScheduleChange]);

    const handleScheduleClick = useCallback(
        async (scheduleValue: string) => {
            if (isLoadingPreview) return;

            setIsLoadingPreview(true);
            setSelectedScheduleForDetails(scheduleValue);

            try {
                const calculation = await onPaymentScheduleChange(scheduleValue);
                setPreviewCalculation(calculation);
                setIsDetailsViewOpen(true);
            } finally {
                setIsLoadingPreview(false);
            }
        },
        [onPaymentScheduleChange, isLoadingPreview],
    );

    const handleCloseDetailsView = useCallback(() => {
        setIsDetailsViewOpen(false);
        setSelectedScheduleForDetails(null);
        setPreviewCalculation(null);
    }, []);

    const paymentScheduleOptions: ChoiceButtonOption[] = useMemo(() => {
        return [
            {
                label: (
                    <div className="flex flex-col">
                        <Paragraph size="medium">{t('eshop.life.fixedPayment')}</Paragraph>
                        <CallbackButton
                            preventDefault
                            className="m-0 w-min self-start p-0 font-normal"
                            variant="text"
                            ignoreFormStatus
                            id={PaymentSchedule.Fixed}
                            onClick={handleScheduleClick}
                            isLoading={isLoadingPreview && selectedScheduleForDetails === PaymentSchedule.Fixed}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('eshop.life.schedule')}
                        </CallbackButton>
                    </div>
                ),
                value: PaymentSchedule.Fixed,
            },
            {
                label: (
                    <div className="flex flex-col">
                        <Paragraph size="medium">{t('eshop.life.increasingPayment')}</Paragraph>
                        <CallbackButton
                            preventDefault
                            className="mx-0 w-min self-start p-0 font-normal"
                            variant="text"
                            ignoreFormStatus
                            id={PaymentSchedule.Increasing}
                            onClick={handleScheduleClick}
                            isLoading={isLoadingPreview && selectedScheduleForDetails === PaymentSchedule.Increasing}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('eshop.life.schedule')}
                        </CallbackButton>
                    </div>
                ),
                value: PaymentSchedule.Increasing,
            },
            {
                label: (
                    <div className="flex flex-col">
                        <Paragraph size="medium">{t('eshop.life.changingPayment')}</Paragraph>
                        <CallbackButton
                            preventDefault
                            className="mx-0 w-min self-start p-0 font-normal"
                            variant="text"
                            ignoreFormStatus
                            id={PaymentSchedule.Changing}
                            onClick={handleScheduleClick}
                            isLoading={isLoadingPreview && selectedScheduleForDetails === PaymentSchedule.Changing}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('eshop.life.schedule')}
                        </CallbackButton>
                    </div>
                ),
                value: PaymentSchedule.Changing,
            },
        ];
    }, [t, handleScheduleClick, isLoadingPreview, selectedScheduleForDetails]);

    return (
        <>
            <ChoiceButtonInput
                name="paymentSchedule"
                defaultChecked={defaultPaymentSchedule}
                options={paymentScheduleOptions}
                onChange={handlePaymentScheduleChange}
            />
            <Message className="mt-8" variant="info">
                {t.rich('eshop.life.changePaymentMessage', {
                    p: children => <Paragraph className="mb-2">{children}</Paragraph>,
                })}
            </Message>
            <PaymentScheduleDetailsView
                paymentSchedule={selectedScheduleForDetails}
                calculation={previewCalculation ?? calculation ?? null}
                open={isDetailsViewOpen}
                onClose={handleCloseDetailsView}
            />
        </>
    );
}
